import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { API_BASE, formatApiErrorDetail } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label, Textarea } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast, Toaster } from "sonner";
import {
  Upload,
  Search,
  FileText,
  Download,
  Eye,
  X,
  Check,
  Ban,
  Trash2,
  Tag,
  Clock,
  Filter,
} from "lucide-react";
import { format, formatDistanceToNow, isBefore } from "date-fns";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";

const MIME_LABEL = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "application/msword": "DOC",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
  "application/vnd.ms-excel": "XLS",
  "image/png": "PNG",
  "image/jpeg": "JPG",
  "image/jpg": "JPG",
  "text/csv": "CSV",
};

const STATUS_META = {
  pending: { label: "Pending approval", variant: "warning" },
  approved: { label: "Approved", variant: "success" },
  rejected: { label: "Rejected", variant: "critical" },
};

const empty = {
  title: "",
  description: "",
  tags: "",
  control_ids: [],
  framework_ids: [],
  expiry_date: "",
  file: null,
};

export default function EvidencePage() {
  const { user } = useAuth();
  const canApprove = user && ["admin", "compliance_officer"].includes(user.role);
  const [items, setItems] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [controls, setControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [mime, setMime] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [frameworkFilter, setFrameworkFilter] = useState("");
  const [expiring, setExpiring] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (mime) params.set("mime_type", mime);
      if (tagFilter) params.set("tag", tagFilter);
      if (frameworkFilter) params.set("framework_id", frameworkFilter);
      if (expiring) params.set("expiring_within_days", expiring);
      const { data } = await api.get(`/compliance/evidence?${params.toString()}`);
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  }, [q, status, mime, tagFilter, frameworkFilter, expiring]);

  useEffect(() => {
    (async () => {
      const [fws, cs] = await Promise.all([
        api.get("/compliance/frameworks"),
        api.get("/compliance/controls"),
      ]);
      setFrameworks(fws.data.items || []);
      setControls(cs.data.items || []);
    })();
  }, []);

  useEffect(() => {
    load();
  }, [status, mime, tagFilter, frameworkFilter, expiring, load]);

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach((e) => (e.tags || []).forEach((t) => s.add(t)));
    return Array.from(s).sort();
  }, [items]);

  const upload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) return toast.error("Please choose a file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("title", uploadForm.title);
      if (uploadForm.description) fd.append("description", uploadForm.description);
      if (uploadForm.tags) fd.append("tags", uploadForm.tags);
      if (uploadForm.control_ids.length) fd.append("control_ids", uploadForm.control_ids.join(","));
      if (uploadForm.framework_ids.length) fd.append("framework_ids", uploadForm.framework_ids.join(","));
      if (uploadForm.expiry_date) {
        // convert to ISO with time 23:59
        fd.append("expiry_date", new Date(uploadForm.expiry_date + "T23:59:59Z").toISOString());
      }
      await api.post("/compliance/evidence", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Evidence uploaded");
      setShowUpload(false);
      setUploadForm(empty);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const approve = async (ev) => {
    try {
      await api.post(`/compliance/evidence/${ev.id}/approve`, { notes: null });
      toast.success("Approved");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };
  const reject = async (ev) => {
    const notes = window.prompt("Rejection notes (optional):", "");
    try {
      await api.post(`/compliance/evidence/${ev.id}/reject`, { notes });
      toast.info("Rejected");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };
  const del = async (ev) => {
    if (!window.confirm(`Delete evidence "${ev.title}"?`)) return;
    try {
      await api.delete(`/compliance/evidence/${ev.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const download = async (ev) => {
    try {
      const token = localStorage.getItem("ccams_token");
      const resp = await fetch(`${API_BASE}/compliance/evidence/${ev.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = ev.filename || ev.title;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const preview = async (ev) => {
    try {
      const token = localStorage.getItem("ccams_token");
      const resp = await fetch(`${API_BASE}/compliance/evidence/${ev.id}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setPreviewing(ev);
    } catch (err) {
      toast.error("Preview failed");
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewing(null);
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Evidence Repository</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Upload, tag, version and approve compliance evidence. Supported: PDF, DOCX, XLSX, PNG, JPG, CSV — up to 10 MB.
          </p>
        </div>
        <Button data-testid="evidence-upload-btn" onClick={() => { setUploadForm(empty); setShowUpload(true); }}>
          <Upload className="w-4 h-4" /> Upload evidence
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                data-testid="evidence-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                className="pl-9"
                placeholder="Search title, description or filename…"
              />
            </div>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              data-testid="evidence-status-filter"
              className="h-10 w-full md:w-44 rounded-md border border-border bg-card px-3 text-sm"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={mime}
              onChange={(e) => setMime(e.target.value)}
              data-testid="evidence-type-filter"
              className="h-10 w-full md:w-40 rounded-md border border-border bg-card px-3 text-sm"
            >
              <option value="">All types</option>
              {Object.entries(MIME_LABEL).filter(([k]) => k !== "image/jpg").map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
            <select
              value={frameworkFilter}
              onChange={(e) => setFrameworkFilter(e.target.value)}
              data-testid="evidence-framework-filter"
              className="h-10 w-full md:w-40 rounded-md border border-border bg-card px-3 text-sm"
            >
              <option value="">All frameworks</option>
              {frameworks.map((f) => (
                <option key={f.id} value={f.id}>{f.code}</option>
              ))}
            </select>
            <select
              value={expiring}
              onChange={(e) => setExpiring(e.target.value)}
              data-testid="evidence-expiring-filter"
              className="h-10 w-full md:w-44 rounded-md border border-border bg-card px-3 text-sm"
            >
              <option value="">Any expiry</option>
              <option value="7">Expires in 7 days</option>
              <option value="30">Expires in 30 days</option>
              <option value="90">Expires in 90 days</option>
            </select>
            <Button variant="outline" onClick={load} data-testid="evidence-search-btn">
              <Filter className="w-4 h-4" /> Apply
            </Button>
          </div>
          {allTags.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground">Tags:</span>
              {allTags.map((t) => (
                <button
                  key={t}
                  data-testid={`evidence-tag-${t}`}
                  onClick={() => setTagFilter(tagFilter === t ? "" : t)}
                  className={cn(
                    "text-xs px-2 py-0.5 rounded-full border transition-colors",
                    tagFilter === t
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border hover:bg-muted"
                  )}
                >
                  #{t}
                </button>
              ))}
              {tagFilter && (
                <button className="text-xs text-muted-foreground underline" onClick={() => setTagFilter("")}>
                  Clear
                </button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="border-t border-b border-border">
                  <th className="text-left py-2.5 px-4 label-eyebrow">Title</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Type</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Tags</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Version</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Status</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Expiry</th>
                  <th className="text-left py-2.5 px-4 label-eyebrow">Uploaded</th>
                  <th className="text-right py-2.5 px-4 label-eyebrow">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Loading…</td></tr>}
                {!loading && items.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">
                    <FileText className="w-8 h-8 mx-auto mb-2" />
                    No evidence found. Upload the first document.
                  </td></tr>
                )}
                {items.map((e) => {
                  const st = STATUS_META[e.status] || STATUS_META.pending;
                  const expired = e.expiry_date && isBefore(new Date(e.expiry_date), new Date());
                  return (
                    <tr key={e.id} data-testid={`evidence-row-${e.id}`} className="border-b border-border hover:bg-muted/40 transition-colors">
                      <td className="py-3 px-4 max-w-xs">
                        <div className="font-medium truncate">{e.title}</div>
                        <div className="text-xs text-muted-foreground truncate">{e.filename}</div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="neutral">{MIME_LABEL[e.mime_type] || "FILE"}</Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {(e.tags || []).slice(0, 3).map((t) => (
                            <span key={t} className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full border border-border text-muted-foreground">
                              <Tag className="w-2.5 h-2.5" /> {t}
                            </span>
                          ))}
                          {(e.tags || []).length > 3 && <span className="text-[10px] text-muted-foreground">+{e.tags.length - 3}</span>}
                        </div>
                      </td>
                      <td className="py-3 px-4 tabular-nums text-muted-foreground">v{e.version}</td>
                      <td className="py-3 px-4"><Badge variant={st.variant}>{st.label}</Badge></td>
                      <td className="py-3 px-4">
                        {e.expiry_date ? (
                          <span className={cn("inline-flex items-center gap-1 text-xs", expired ? "text-red-600" : "text-muted-foreground")}>
                            <Clock className="w-3 h-3" />
                            {format(new Date(e.expiry_date), "MMM d, yyyy")}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {e.created_at && formatDistanceToNow(new Date(e.created_at), { addSuffix: true })}
                        {e.uploaded_by_name && <div className="truncate">{e.uploaded_by_name}</div>}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex gap-1">
                          <Button size="icon" variant="outline" onClick={() => preview(e)} data-testid={`evidence-preview-${e.id}`} title="Preview">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="outline" onClick={() => download(e)} data-testid={`evidence-download-${e.id}`} title="Download">
                            <Download className="w-4 h-4" />
                          </Button>
                          {canApprove && e.status === "pending" && (
                            <>
                              <Button size="icon" variant="success" onClick={() => approve(e)} data-testid={`evidence-approve-${e.id}`} title="Approve">
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="icon" variant="destructive" onClick={() => reject(e)} data-testid={`evidence-reject-${e.id}`} title="Reject">
                                <Ban className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {canApprove && (
                            <Button size="icon" variant="destructive" onClick={() => del(e)} data-testid={`evidence-delete-${e.id}`} title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto card-surface animate-fade-in" onClick={(e) => e.stopPropagation()} data-testid="evidence-upload-modal">
            <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-card">
              <h3 className="text-base font-semibold">Upload evidence</h3>
              <button onClick={() => setShowUpload(false)} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={upload} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>File (PDF, DOCX, XLSX, PNG, JPG, CSV — max 10 MB)</Label>
                <input
                  ref={fileRef}
                  data-testid="evidence-file"
                  type="file"
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.png,.jpg,.jpeg,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg,text/csv"
                  onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                  className="mt-1.5 block w-full text-sm text-foreground file:mr-3 file:h-10 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground file:font-medium hover:file:bg-primary-hover"
                  required
                />
              </div>
              <div className="md:col-span-2">
                <Label>Title</Label>
                <Input data-testid="evidence-title" className="mt-1.5" value={uploadForm.title} onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })} required />
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea className="mt-1.5" value={uploadForm.description} onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label>Tags (comma separated)</Label>
                <Input data-testid="evidence-tags" className="mt-1.5" value={uploadForm.tags} onChange={(e) => setUploadForm({ ...uploadForm, tags: e.target.value })} placeholder="policy, network, quarterly" />
              </div>
              <div>
                <Label>Related frameworks</Label>
                <select
                  multiple
                  value={uploadForm.framework_ids}
                  onChange={(e) => setUploadForm({ ...uploadForm, framework_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                  className="mt-1.5 w-full rounded-md border border-border bg-card px-3 text-sm min-h-[80px]"
                >
                  {frameworks.map((f) => <option key={f.id} value={f.id}>{f.code} — {f.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Related controls</Label>
                <select
                  multiple
                  value={uploadForm.control_ids}
                  onChange={(e) => setUploadForm({ ...uploadForm, control_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                  className="mt-1.5 w-full rounded-md border border-border bg-card px-3 text-sm min-h-[80px]"
                >
                  {controls.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
              <div>
                <Label>Expiry date (optional)</Label>
                <Input className="mt-1.5" type="date" value={uploadForm.expiry_date} onChange={(e) => setUploadForm({ ...uploadForm, expiry_date: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex justify-end gap-2 sticky bottom-0 bg-card pt-2 pb-1">
                <Button type="button" variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
                <Button type="submit" data-testid="evidence-upload-submit" disabled={uploading}>
                  <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="w-full max-w-4xl h-[85vh] card-surface animate-fade-in flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="min-w-0">
                <div className="text-sm font-semibold truncate">{previewing.title}</div>
                <div className="text-xs text-muted-foreground truncate">{previewing.filename}</div>
              </div>
              <button onClick={closePreview} className="p-1 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 bg-muted overflow-auto">
              {previewing.mime_type?.startsWith("image/") ? (
                <img src={previewUrl} alt={previewing.title} className="max-w-full mx-auto" />
              ) : previewing.mime_type === "application/pdf" ? (
                <iframe title="Preview" src={previewUrl} className="w-full h-full" />
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  Preview not supported for this file type. Use Download instead.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
